
import React from 'react';
import { HistoryItem, Language } from '../types';
import { translations } from '../translations';
import { X, Clock, ChevronRight, Trash2, Mountain } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  items: HistoryItem[];
  onSelect: (item: HistoryItem) => void;
  onDelete: (id: string, e: React.MouseEvent) => void;
  onClearAll: () => void;
  lang: Language;
}

export const HistoryDrawer: React.FC<Props> = ({ isOpen, onClose, items, onSelect, onDelete, onClearAll, lang }) => {
  const t = translations[lang];
  return (
    <>
      <div className={`fixed inset-0 bg-stone-900/50 backdrop-blur-sm z-50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={onClose} />
      <div className={`fixed top-0 right-0 h-full w-full max-w-sm bg-[#fafaf9] shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header wrapper for Safe Area background continuity */}
        <div className="bg-stone-100 border-b border-stone-200 safe-area-top">
          <div className="flex items-center justify-between p-4 md:p-5">
            <div className="flex items-center gap-2 text-stone-800">
               <div className="p-1.5 bg-amber-100 rounded text-amber-800"><Clock size={18} /></div>
               <div>
                 <h2 className="font-serif-sc font-bold text-lg leading-none">{t.historyTitle}</h2>
                 <span className="text-[10px] text-stone-500 uppercase tracking-widest">{t.historySub}</span>
               </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-stone-200 rounded-full transition-colors text-stone-500"><X size={20} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#fafaf9]" style={{ backgroundImage: 'linear-gradient(#e5e7eb 1px, transparent 1px)', backgroundSize: '100% 32px' }}>
           {items.length === 0 ? (
             <div className="h-full flex flex-col items-center justify-center text-stone-400 opacity-60">
                <Mountain size={48} strokeWidth={1} className="mb-4" />
                <p className="font-serif-sc">{t.historyEmpty}</p>
                <p className="text-xs mt-2">{t.historyEmptySub}</p>
             </div>
           ) : (
             items.map((item) => (
               <div key={item.id} onClick={() => onSelect(item)} className="group relative bg-white border border-stone-200 rounded-lg p-2 shadow-sm hover:shadow-md transition-all cursor-pointer flex gap-3 items-center">
                 <div className="w-16 h-16 shrink-0 rounded bg-stone-100 overflow-hidden border border-stone-100">
                    <img src={item.thumbnail} alt={item.data.name} className="w-full h-full object-cover" />
                 </div>
                 <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between mb-1">
                      <h3 className="font-serif-sc font-bold text-stone-800 truncate pr-2">{item.data.name}</h3>
                      <span className="text-[10px] text-stone-400 whitespace-nowrap">{new Date(item.timestamp).toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' })}</span>
                    </div>
                    <p className="text-xs text-stone-500 truncate mb-1">{item.data.category}</p>
                    <div className="flex items-center gap-2">
                       {item.data.isThinSection && <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded flex-none">{t.thinTag}</span>}
                       {item.data.economicAnalysis?.prospectingPotential?.includes(lang === 'zh' ? '潜力' : 'Potential') && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded flex-none">{t.potentialTag}</span>}
                    </div>
                 </div>
                 <ChevronRight size={16} className="text-stone-300 group-hover:text-amber-500 transition-colors" />
                 <button onClick={(e) => onDelete(item.id, e)} className="absolute -top-2 -right-2 bg-white text-red-400 p-1 rounded-full shadow border border-stone-100 opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-600 hover:bg-red-50"><Trash2 size={12} /></button>
               </div>
             ))
           )}
        </div>

        {/* Footer wrapper for Safe Area bottom */}
        {items.length > 0 && (
          <div className="bg-white border-t border-stone-200 safe-area-bottom">
            <div className="p-4">
              <button onClick={() => { if(window.confirm(lang === 'en' ? 'Clear all records?' : '确定要清空所有记录吗？')) onClearAll(); }} className="w-full py-3 border border-stone-300 rounded-lg text-stone-500 text-sm hover:bg-stone-50 hover:text-red-500 transition-colors font-serif-sc">
                {t.clearHistory}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
